import type React from "react";
import { useEffect, useState } from "react";
import type { HelpQuestionsReport } from "@dentacore/shared-types";

import { Pager, useFitPages } from "../ui/paging";
import styles from "./operator-page.module.css";
import { operatorHelpQuestions } from "./operator-api";

const SPANS = [7, 30, 90] as const;

/** `2026-11-02T…` as `2 Nov`. */
const day = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

/**
 * **What the help panel could not answer** (STORY-226, HP-D8).
 *
 * One row per wording, most asked first, across every clinic: the words, the
 * page they were asked on most, how many times, in how many clinics, and when
 * last. Nobody is named and no clinic is either — the table keeps no person
 * (HP-D7), and a gap is DentaCore's to fill, not a clinic's to be told about.
 * Each row is a help article somebody still needs to write.
 */
export function HelpQuestionsSection(): React.JSX.Element {
  const [days, setDays] = useState<(typeof SPANS)[number]>(30);
  const [report, setReport] = useState<HelpQuestionsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReport(null);
    setError(null);
    void operatorHelpQuestions(days)
      .then(setReport)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not read the help questions");
      });
  }, [days]);

  const groups = report?.groups ?? [];
  const { areaRef, measure, page, setPage, pages, range } = useFitPages({
    keys: groups.map((g) => g.question),
    estimate: () => 44,
    gap: 0,
    resetOn: days,
  });

  return (
    <div className={`${styles.console} ${styles.fitPage}`} data-testid="operator-help-questions">
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Help questions</h1>
          <p className={styles.muted}>
            What people asked the help panel that no article answered. Each row is a guide to write.
          </p>
        </div>
      </header>

      <div className={styles.lanes} role="tablist" aria-label="How far back">
        {SPANS.map((span) => (
          <button
            key={span}
            type="button"
            role="tab"
            aria-selected={days === span}
            className={days === span ? styles.laneOn : styles.lane}
            onClick={() => {
              setDays(span);
            }}
            data-testid={`help-span-${String(span)}`}
          >
            Last {span} days
            {days === span && report !== null && (
              <span className={styles.laneCount}>{report.total}</span>
            )}
          </button>
        ))}
      </div>

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <section className={styles.tabCard}>
        <div className={styles.tabBody}>
          {report !== null && groups.length === 0 ? (
            <p className={styles.muted} data-testid="help-questions-empty">
              Nothing unanswered in the last {days} days.
            </p>
          ) : (
            <>
              <div className={styles.helpHead} aria-hidden="true">
                <span>Asked</span>
                <span>Question</span>
                <span>Page</span>
                <span>Clinics</span>
                <span>Last asked</span>
              </div>
              <div className={styles.fitList} ref={areaRef}>
                <ul className={styles.rows} data-testid="help-questions-list">
                  {report === null && <li className={styles.muted}>Reading…</li>}
                  {groups.slice(range[0], range[1]).map((group) => (
                    <li key={group.question} ref={measure(group.question)}>
                      <div className={styles.helpRow} data-testid="help-question-row">
                        <span className={styles.helpAsked}>{group.asked}</span>
                        <span className={styles.helpQuestion}>{group.question}</span>
                        <span className={styles.helpPage}>{group.page}</span>
                        <span className={styles.figureCell}>{group.clinics}</span>
                        <span className={styles.muted}>{day(group.lastAskedAt)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <Pager
                page={page}
                count={pages.length}
                onPage={setPage}
                from={range[0] + 1}
                to={range[1]}
                total={groups.length}
                noun="questions"
                testId="help-questions-pager"
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
